import { useEffect, useState } from "preact/hooks";
import QRCode from "qrcode";
import { toast } from "react-hot-toast";
import {
  apiClient,
  temporaryNumberAlreadyAllocated,
  temporaryNumberDisclaimer,
} from "../../constants.ts";
import { Button, CopyButton, Spinner } from "./components.tsx";
import { useAuth, useQuery } from "./db.ts";
import { AuthButtons } from "./login.tsx";

export const getWhatsappWizardRedirectUrl = () => {
  if (typeof globalThis.location === "undefined") return undefined;
  const url = new URL(globalThis.location.href);
  url.searchParams.set("wizard", "whatsapp");
  return url.toString();
};

type CountryOption = {
  countryId: string;
  name: string;
  iso: string;
  priceUsd: number;
};

type TemporaryCountryOption = {
  countryId: number;
  name: string;
  costUsd: number;
  available: number;
};

type SmsMessage = {
  id: string;
  fromNumber: string;
  body: string;
  receivedAt: number;
  verificationCode?: string;
};

type WizardStep = "choose_number" | "activate_phone" | "scan_qr" | "connected";

export const sortCountriesByName = <T extends { name: string }>(
  countries: T[],
): T[] =>
  countries.toSorted((a, b) => (a.name || "").localeCompare(b.name || ""));

export const isMobileViewport = () =>
  typeof globalThis.matchMedia === "function"
    ? globalThis.matchMedia("(max-width: 768px)").matches
    : false;

export const DedicatedWhatsappWizard = ({
  botId,
  token,
  isOpen,
  onClose,
  onSuccess,
}: {
  botId: string;
  token: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (phoneNumber: string) => void;
}) => {
  const { user } = useAuth();
  const { data: botData } = useQuery(
    botId ? { bots: { $: { where: { id: botId } } } } : null,
  );
  const initialBotNumber =
    botData?.bots?.[0]?.twilioPhoneNumber?.replace(/\D/g, "") ?? "";
  const [step, setStep] = useState<WizardStep>(
    initialBotNumber ? "activate_phone" : "choose_number",
  );
  const [tab, setTab] = useState<"buy" | "own">("buy");
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [temporaryCountries, setTemporaryCountries] = useState<
    TemporaryCountryOption[]
  >([]);
  const [selectedTemporaryCountryId, setSelectedTemporaryCountryId] = useState<
    number | null
  >(null);
  const [countryLoading, setCountryLoading] = useState(false);
  const [availability, setAvailability] = useState<
    {
      available: number;
      priceUsd: number;
      sampleNumber?: string;
    } | null
  >(null);

  const [ownNumber, setOwnNumber] = useState("");
  const [activeNumber, setActiveNumber] = useState(initialBotNumber);
  const [numberType, setNumberType] = useState<"temporary" | "constant">(
    "temporary",
  );
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<string | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoValidating, setPromoValidating] = useState(false);

  const handleApplyPromo = async () => {
    setPromoError(null);
    const normalized = promoCodeInput.trim().toUpperCase();
    if (!normalized) return;
    setPromoValidating(true);
    try {
      const res = await apiClient({
        endpoint: "validate-promo-code",
        payload: { code: normalized },
      });
      if (res.valid) {
        setAppliedPromo(normalized);
        toast.success("Promo code applied: 1st month free!");
      } else {
        setPromoError(res.error || "Invalid promo code");
      }
    } catch {
      setPromoError("Failed to validate promo code");
    } finally {
      setPromoValidating(false);
    }
  };

  const { data: userData } = useQuery(
    user?.id ? { $users: { $: { where: { id: user.id } } } } : null,
  );
  const userPhone = userData?.$users?.[0]?.phone;
  const needsPhoneVerification = !userPhone &&
    (numberType === "temporary" || Boolean(appliedPromo));

  const botNumber = botData?.bots?.[0]?.twilioPhoneNumber;
  useEffect(() => {
    if (botNumber && !activeNumber) {
      const cleaned = botNumber.replace(/\D/g, "");
      if (cleaned) {
        setActiveNumber(cleaned);
        setStep((s) => s === "choose_number" ? "activate_phone" : s);
      }
    }
  }, [botNumber, activeNumber]);

  const [verifyPhoneInput, setVerifyPhoneInput] = useState("");
  const [otpPinInput, setOtpPinInput] = useState("");
  const [pinSent, setPinSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const handleSendPhoneOtp = async () => {
    if (!token || !verifyPhoneInput.trim()) return;
    setSendingOtp(true);
    try {
      const digits = verifyPhoneInput.replace(/\D/g, "");
      const res = await apiClient({
        endpoint: "request-phone-link",
        token,
        payload: { phone: digits },
      });
      if (res.success) {
        setPinSent(true);
        toast.success("Verification code sent via SMS!");
      } else {
        toast.error(
          res.error === "this-phone-belongs-to-another-web-account"
            ? "This phone is already linked to another prompt2bot account. Please sign in with that account, or choose a permanent line."
            : res.error === "too-many-otp-attempts-for-this-number"
            ? "Too many attempts for this number. Please try again tomorrow."
            : res.error || "Failed to send code",
        );
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to send code");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleConfirmPhoneOtp = async () => {
    if (!token || !otpPinInput.trim()) return;
    setVerifyingOtp(true);
    try {
      const digits = verifyPhoneInput.replace(/\D/g, "");
      const res = await apiClient({
        endpoint: "verify-phone-link",
        token,
        payload: { phone: digits, pin: otpPinInput.trim() },
      });
      if (res.success) {
        toast.success("Phone verified successfully!");
        setPinSent(false);
        setOtpPinInput("");
      } else {
        toast.error(
          res.error === "invalid-pin"
            ? "Invalid code. Please try again."
            : res.error || "Verification failed",
        );
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Verification failed");
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Step 2: SMS listener
  const [smsMessages, setSmsMessages] = useState<SmsMessage[]>([]);
  const [latestCode, setLatestCode] = useState<string | null>(null);

  // Step 3: QR / Pairing
  const [preferCode, setPreferCode] = useState(isMobileViewport);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [isProvisioning, setIsProvisioning] = useState(false);

  useEffect(() => {
    if (!isOpen || !token || !user?.email) return;
    apiClient({
      endpoint: "list-number-countries",
      token,
      payload: {},
    }).then((res) => {
      if (res.success && res.countries.length > 0) {
        const sorted = sortCountriesByName(res.countries);
        setCountries(sorted);
        const il = sorted.find((c) => c.iso === "IL" || c.countryId === "93");
        setSelectedCountryId(il ? il.countryId : sorted[0].countryId);
      }
    }).catch((err) => {
      console.error("Failed to fetch country list:", err);
    });
  }, [isOpen, token, user?.email]);

  useEffect(() => {
    if (!isOpen || !token || !user?.email) return;
    apiClient({
      endpoint: "list-hero-sms-countries",
      token,
      payload: {},
    }).then((res) => {
      if (res.success) {
        setTemporaryCountries(sortCountriesByName(res.countries));
      }
    }).catch((err) => {
      console.error("Failed to fetch temporary number countries:", err);
    });
  }, [isOpen, token, user?.email]);

  useEffect(() => {
    if (!selectedCountryId || !token || !user?.email) return;
    setCountryLoading(true);
    setError(null);
    apiClient({
      endpoint: "get-number-country-availability",
      token,
      payload: { countryId: selectedCountryId },
    }).then((res) => {
      if (res.success) {
        setAvailability({
          available: res.available,
          priceUsd: res.priceUsd,
          sampleNumber: res.sampleNumber,
        });
      } else {
        setError(res.error);
      }
    }).catch((err) => {
      console.error("Failed to check availability:", err);
    }).finally(() => {
      setCountryLoading(false);
    });
  }, [selectedCountryId, token, user?.email]);

  // Check if returning from checkout
  useEffect(() => {
    if (!isOpen || !token || !botId || !user?.email) return;
    const url = new URL(globalThis.location.href);
    if (url.searchParams.get("checkout") === "success") {
      const countryParam = url.searchParams.get("countryId") || undefined;
      setIsPurchasing(true);
      apiClient({
        endpoint: "buy-dedicated-number",
        token,
        payload: { botId, countryId: countryParam },
      }).then((res) => {
        if (res.success) {
          setActiveNumber(res.phoneNumber);
          setStep("activate_phone");
          toast.success("Dedicated number activated!");
        } else {
          setError(res.error);
        }
      }).catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
      }).finally(() => {
        setIsPurchasing(false);
      });
    }
  }, [isOpen, token, botId]);

  // Handle number purchase or checkout
  const handleBuyNumber = async () => {
    if (!token || !botId) return;
    if (needsPhoneVerification) {
      setError(
        "Please verify your mobile phone number to continue. Temporary numbers are limited to one per verified account.",
      );
      return;
    }
    setIsPurchasing(true);
    setError(null);
    try {
      const buyRes = await apiClient({
        endpoint: "buy-dedicated-number",
        token,
        payload: {
          botId,
          countryId: selectedCountryId,
          heroSmsCountryId: selectedTemporaryCountryId ?? undefined,
          promoCode: appliedPromo ?? undefined,
          numberType,
        },
      });

      if (buyRes.success) {
        setActiveNumber(buyRes.phoneNumber);
        if (buyRes.numberType) {
          setNumberType(buyRes.numberType);
        }
        setStep("activate_phone");
        toast.success(`Number +${buyRes.phoneNumber} allocated!`);
        return;
      }

      // If capacity required, trigger Stripe checkout session
      if (
        buyRes.error.toLowerCase().includes("capacity") ||
        buyRes.error.toLowerCase().includes("budget") ||
        buyRes.error.toLowerCase().includes("upgrade")
      ) {
        const currentUrl = new URL(globalThis.location.href);
        currentUrl.searchParams.set("checkout", "success");
        if (selectedCountryId) {
          currentUrl.searchParams.set("countryId", selectedCountryId);
        }
        const checkoutRes = await apiClient({
          endpoint: "create-number-checkout-session",
          token,
          payload: {
            botId,
            countryId: selectedCountryId,
            successUrl: currentUrl.toString(),
          },
        });

        if (checkoutRes.success) {
          globalThis.location.href = checkoutRes.url;
          return;
        }
        throw new Error(
          checkoutRes.error || "Failed to create payment session",
        );
      }

      throw new Error(buyRes.error);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleUseOwnNumber = () => {
    const cleaned = ownNumber.replace(/\D/g, "");
    if (cleaned.length < 8) {
      setError("Please enter a valid phone number with country code.");
      return;
    }
    setError(null);
    setActiveNumber(cleaned);
    setStep("activate_phone");
  };

  // Poll for SMS when in activate_phone or scan_qr step
  useEffect(() => {
    if (
      (step !== "activate_phone" && step !== "scan_qr") ||
      !activeNumber ||
      !token
    ) {
      return;
    }
    let isCancelled = false;

    const pollSms = () => {
      apiClient({
        endpoint: "get-number-sms",
        token,
        payload: { phoneNumber: activeNumber },
      }).then((res) => {
        if (!isCancelled) {
          if (res.success) {
            setSmsMessages(res.messages);
            const found = res.messages.find((m) => !!m.verificationCode);
            if (found?.verificationCode) {
              setLatestCode(found.verificationCode);
            }
          } else {
            setError(res.error);
          }
        }
      }).catch(() => {});
    };

    pollSms();
    const interval = setInterval(pollSms, 3000);
    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [step, activeNumber, token]);

  // Start provisioning when entering scan_qr step
  useEffect(() => {
    if (step !== "scan_qr" || !activeNumber || !token || !botId) return;
    setIsProvisioning(true);
    setError(null);

    apiClient({
      endpoint: "start-whatsapp-provisioning",
      token,
      payload: {
        botId,
        phoneNumber: activeNumber,
        preferCodeLogin: preferCode,
      },
    }).then((res) => {
      if (res.success) {
        if (res.isConnected) {
          setStep("connected");
          onSuccess(activeNumber);
          return;
        }
        if (res.pairingCode) setPairingCode(res.pairingCode);
        if (res.verificationCode) {
          QRCode.toString(res.verificationCode, {
            type: "svg",
            margin: 1,
            width: 220,
          }).then(setQrSvg).catch(console.error);
        }
      } else {
        setError(res.error);
      }
    }).catch((err) => {
      setError(err instanceof Error ? err.message : String(err));
    }).finally(() => {
      setIsProvisioning(false);
    });

    // Poll connection status
    const pollConnection = setInterval(() => {
      apiClient({
        endpoint: "check-whatsapp-connection",
        token,
        payload: { botId, phoneNumber: activeNumber },
      }).then((res) => {
        if (res.success) {
          if (res.isConnected) {
            clearInterval(pollConnection);
            setStep("connected");
            onSuccess(activeNumber);
            toast.success("WhatsApp successfully linked!");
          } else {
            if (res.pairingCode) setPairingCode(res.pairingCode);
            if (res.verificationCode) {
              QRCode.toString(res.verificationCode, {
                type: "svg",
                margin: 1,
                width: 220,
              }).then(setQrSvg).catch(console.error);
            }
          }
        }
      }).catch(() => {});
    }, 2500);

    return () => clearInterval(pollConnection);
  }, [step, activeNumber, preferCode, token, botId]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-xl bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle rounded-2xl shadow-2xl p-6 sm:p-8 flex flex-col gap-6 text-left my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with step indicators */}
        <div className="flex flex-col gap-2 border-b border-border-subtle-light dark:border-border-subtle pb-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-text-primary-light dark:text-text-primary font-display flex items-center gap-2">
                <span>💬</span> Make me a WhatsApp Account
              </h2>
              <p className="text-xs text-text-secondary-light dark:text-text-secondary mt-1">
                Set up a dedicated WhatsApp line on your phone and link it to
                your bot.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-text-tertiary-light dark:text-text-tertiary hover:text-text-primary-light dark:hover:text-text-primary text-xl font-bold p-1 cursor-pointer self-start"
            >
              ✕
            </button>
          </div>

          <div className="flex items-center gap-2 text-xs font-semibold text-text-tertiary-light dark:text-text-tertiary mt-2">
            <button
              type="button"
              onClick={() => setStep("choose_number")}
              className={`px-2.5 py-1 rounded-full cursor-pointer transition-colors ${
                step === "choose_number"
                  ? "bg-accent text-white"
                  : "bg-surface-overlay-light dark:bg-surface-overlay hover:text-text-primary"
              }`}
            >
              1. Number
            </button>
            <span>→</span>
            <button
              type="button"
              disabled={!activeNumber}
              onClick={() => activeNumber && setStep("activate_phone")}
              className={`px-2.5 py-1 rounded-full transition-colors ${
                step === "activate_phone"
                  ? "bg-accent text-white"
                  : "bg-surface-overlay-light dark:bg-surface-overlay"
              } ${
                activeNumber
                  ? "cursor-pointer hover:text-text-primary"
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              2. Activate
            </button>
            <span>→</span>
            <button
              type="button"
              disabled={!activeNumber}
              onClick={() => activeNumber && setStep("scan_qr")}
              className={`px-2.5 py-1 rounded-full transition-colors ${
                step === "scan_qr" || step === "connected"
                  ? "bg-accent text-white"
                  : "bg-surface-overlay-light dark:bg-surface-overlay"
              } ${
                activeNumber
                  ? "cursor-pointer hover:text-text-primary"
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              3. Link Bot
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-600 dark:text-rose-400 text-xs font-medium">
            {error}
          </div>
        )}

        {!user?.email
          ? (
            <div className="flex flex-col items-center justify-center gap-4 py-6 text-center w-full">
              <div className="text-4xl">✉️</div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary font-display">
                  Add an Email to Continue
                </h3>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary max-w-sm mx-auto mt-1">
                  To connect a dedicated WhatsApp line or purchase a number,
                  please upgrade your guest account with an email.
                </p>
              </div>
              <div className="w-full max-w-sm mt-2">
                <AuthButtons
                  showGuest={false}
                  buttonTextPrefix="Continue"
                  redirectUrl={getWhatsappWizardRedirectUrl()}
                  onSuccess={() => {
                    toast.success("Account upgraded!");
                  }}
                />
              </div>
            </div>
          )
          : (
            <>
              {/* STEP 1: CHOOSE NUMBER */}
              {step === "choose_number" && (
                <div className="space-y-5">
                  {/* PREREQUISITE REQUIREMENT NOTICE */}
                  <div className="p-3 bg-sky-500/10 border border-sky-500/30 rounded-xl flex items-center gap-3 text-xs text-sky-950 dark:text-sky-100">
                    <span className="text-lg leading-none shrink-0">📱</span>
                    <p className="text-sky-900 dark:text-sky-200 leading-snug">
                      <strong>How it works:</strong>{" "}
                      Pick a number for your bot below → Add it to WhatsApp on
                      your phone (Settings → Add account) → Scan QR to link.
                    </p>
                  </div>

                  <div className="flex rounded-xl bg-surface-overlay-light dark:bg-surface-overlay p-1 border border-border-subtle-light dark:border-border-subtle">
                    <button
                      type="button"
                      onClick={() => {
                        setTab("buy");
                        setError(null);
                      }}
                      className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                        tab === "buy"
                          ? "bg-surface-light dark:bg-surface text-accent shadow-xs"
                          : "text-text-secondary-light dark:text-text-secondary hover:text-text-primary"
                      }`}
                    >
                      Buy a dedicated number
                      {appliedPromo
                        ? " (Free 1st mo)"
                        : availability?.priceUsd
                        ? ` ($${availability.priceUsd}/mo)`
                        : ""}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTab("own");
                        setError(null);
                      }}
                      className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                        tab === "own"
                          ? "bg-surface-light dark:bg-surface text-accent shadow-xs"
                          : "text-text-secondary-light dark:text-text-secondary hover:text-text-primary"
                      }`}
                    >
                      Use my own number
                    </button>
                  </div>

                  {tab === "buy"
                    ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div
                            onClick={() => {
                              setNumberType("temporary");
                              setError(null);
                            }}
                            className={`p-3.5 rounded-xl border transition-all cursor-pointer text-left space-y-2 ${
                              numberType === "temporary"
                                ? "border-accent bg-accent/5 ring-1 ring-accent"
                                : "border-border-default-light dark:border-border-default bg-surface-overlay-light dark:bg-surface-overlay hover:border-border-subtle-light dark:hover:border-border-subtle"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold text-text-primary-light dark:text-text-primary">
                                Temporary Number
                              </span>
                              <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                                Free
                              </span>
                            </div>
                            <p className="text-[11px] text-text-secondary-light dark:text-text-secondary leading-relaxed">
                              Quick setup, no payment needed. Activated via SMS.
                              One per verified account. The number is rented,
                              not yours.
                            </p>
                            <div className="text-[10px] text-text-tertiary-light dark:text-text-tertiary space-y-0.5 pt-1 border-t border-border-subtle-light dark:border-border-subtle">
                              <div>✓ Instant SMS code in wizard</div>
                              <div>✓ No subscription</div>
                              <div>✗ Inbound voice calls not supported</div>
                              <div>✗ Not your number, can be reclaimed</div>
                            </div>
                            <p className="text-[10px] text-amber-700 dark:text-amber-300 leading-relaxed bg-amber-500/10 border border-amber-500/20 rounded-lg p-2">
                              {temporaryNumberDisclaimer}
                            </p>
                          </div>

                          <div
                            onClick={() => {
                              setNumberType("constant");
                              setError(null);
                            }}
                            className={`p-3.5 rounded-xl border transition-all cursor-pointer text-left space-y-2 ${
                              numberType === "constant"
                                ? "border-accent bg-accent/5 ring-1 ring-accent"
                                : "border-border-default-light dark:border-border-default bg-surface-overlay-light dark:bg-surface-overlay hover:border-border-subtle-light dark:hover:border-border-subtle"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold text-text-primary-light dark:text-text-primary">
                                Permanent Dedicated
                              </span>
                              <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20">
                                Voice + Calls
                              </span>
                            </div>
                            <p className="text-[11px] text-text-secondary-light dark:text-text-secondary leading-relaxed">
                              Dedicated line held permanently via PBXme. Allows
                              direct voice phone calls to your bot. Verified by
                              phone call (auto-transcribed!).
                            </p>
                            <div className="text-[10px] text-text-tertiary-light dark:text-text-tertiary space-y-0.5 pt-1 border-t border-border-subtle-light dark:border-border-subtle">
                              <div>✓ Direct phone calls to Mentat</div>
                              <div>✓ Permanent ownership</div>
                              <div>
                                {appliedPromo
                                  ? `✓ 1st month free ($${
                                    availability?.priceUsd || 4
                                  }/mo after)`
                                  : `$${availability?.priceUsd || 4}/month`}
                              </div>
                            </div>
                          </div>
                        </div>

                        {numberType === "constant"
                          ? (
                            <div className="space-y-2">
                              <label className="text-xs font-semibold text-text-primary-light dark:text-text-primary">
                                Select country
                              </label>
                              <select
                                value={selectedCountryId}
                                onChange={(e) =>
                                  setSelectedCountryId(e.currentTarget.value)}
                                className="w-full px-3 py-2 text-sm bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-xl text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent cursor-pointer"
                              >
                                {sortCountriesByName(countries).map((c) => (
                                  <option key={c.countryId} value={c.countryId}>
                                    {c.name} {c.iso ? `(${c.iso})` : ""}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )
                          : (
                            <div className="space-y-2">
                              <label className="text-xs font-semibold text-text-primary-light dark:text-text-primary">
                                Temporary number country
                              </label>
                              <select
                                value={selectedTemporaryCountryId ?? ""}
                                onChange={(e) =>
                                  setSelectedTemporaryCountryId(
                                    Number(e.currentTarget.value) || null,
                                  )}
                                className="w-full px-3 py-2 text-sm bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-xl text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent cursor-pointer"
                              >
                                <option value="" disabled>
                                  {temporaryCountries.length === 0
                                    ? "No country in stock right now"
                                    : "Select the country of your number"}
                                </option>
                                {sortCountriesByName(temporaryCountries).map((
                                  c,
                                ) => (
                                  <option key={c.countryId} value={c.countryId}>
                                    {c.name} ({c.available.toLocaleString()}
                                    {" "}
                                    available)
                                  </option>
                                ))}
                              </select>
                              <p className="text-[10px] text-text-tertiary-light dark:text-text-tertiary leading-relaxed">
                                Pick the country your WhatsApp will register
                                with. The number must be able to receive the
                                WhatsApp verification SMS there.
                              </p>
                            </div>
                          )}

                        <div className="space-y-1.5">
                          <label className="text-xs font-semibold text-text-primary-light dark:text-text-primary">
                            Promo code
                          </label>
                          {appliedPromo
                            ? (
                              <div className="flex items-center justify-between p-2.5 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-xs text-emerald-700 dark:text-emerald-300">
                                <span className="font-semibold">
                                  Code {appliedPromo} applied (1st month free)
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAppliedPromo(null);
                                    setPromoCodeInput("");
                                  }}
                                  className="text-text-tertiary hover:text-text-primary text-xs underline cursor-pointer"
                                >
                                  Remove
                                </button>
                              </div>
                            )
                            : (
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  placeholder="Enter promo code (e.g. MENTAT-PRELAUNCH)"
                                  value={promoCodeInput}
                                  onInput={(e) =>
                                    setPromoCodeInput(e.currentTarget.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleApplyPromo();
                                    }
                                  }}
                                  className="flex-1 px-3 py-2 text-xs bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-xl text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                                />
                                <Button
                                  type="button"
                                  onClick={handleApplyPromo}
                                  disabled={!promoCodeInput.trim() ||
                                    promoValidating}
                                  className="px-3 py-2 text-xs font-medium cursor-pointer"
                                >
                                  {promoValidating ? "Checking..." : "Apply"}
                                </Button>
                              </div>
                            )}
                          {promoError && (
                            <p className="text-[11px] text-rose-500 dark:text-rose-400">
                              {promoError}
                            </p>
                          )}
                          {needsPhoneVerification && (
                            <div className="p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-xl space-y-2.5">
                              <div>
                                <span className="text-xs font-semibold text-amber-900 dark:text-amber-200 block">
                                  Quick verification: Your personal mobile
                                  number
                                </span>
                                <span className="text-[11px] text-text-secondary-light dark:text-text-secondary">
                                  To prevent bot spam, we verify your personal
                                  mobile phone with a 1-time SMS code. (Your bot
                                  will get its own separate number in the next
                                  step!)
                                </span>
                              </div>
                              {!pinSent
                                ? (
                                  <div className="flex gap-2">
                                    <input
                                      type="tel"
                                      placeholder="Your personal mobile (e.g. +972501234567)"
                                      value={verifyPhoneInput}
                                      onInput={(e) =>
                                        setVerifyPhoneInput(
                                          e.currentTarget.value,
                                        )}
                                      className="flex-1 px-3 py-1.5 text-xs bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-xl font-mono text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                                    />
                                    <Button
                                      type="button"
                                      onClick={handleSendPhoneOtp}
                                      disabled={sendingOtp ||
                                        !verifyPhoneInput.trim()}
                                      className="px-3 py-1.5 text-xs font-medium cursor-pointer"
                                    >
                                      {sendingOtp
                                        ? "Sending..."
                                        : "Send SMS Code"}
                                    </Button>
                                  </div>
                                )
                                : (
                                  <div className="space-y-2">
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        placeholder="Enter 6-digit SMS code"
                                        value={otpPinInput}
                                        onInput={(e) =>
                                          setOtpPinInput(e.currentTarget.value)}
                                        className="flex-1 px-3 py-1.5 text-xs bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-xl font-mono text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent text-center tracking-widest"
                                      />
                                      <Button
                                        type="button"
                                        onClick={handleConfirmPhoneOtp}
                                        disabled={verifyingOtp ||
                                          !otpPinInput.trim()}
                                        className="px-3 py-1.5 text-xs font-medium cursor-pointer bg-emerald-600 hover:bg-emerald-700 text-white"
                                      >
                                        {verifyingOtp
                                          ? "Verifying..."
                                          : "Verify"}
                                      </Button>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => setPinSent(false)}
                                      className="text-[11px] text-text-tertiary hover:text-text-primary underline cursor-pointer"
                                    >
                                      Change phone number
                                    </button>
                                  </div>
                                )}
                            </div>
                          )}
                          {appliedPromo && userPhone && (
                            <div className="flex items-center gap-2 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-xs text-emerald-700 dark:text-emerald-300">
                              <span className="font-semibold">
                                ✓ Verified mobile: +{userPhone}
                              </span>
                            </div>
                          )}
                        </div>

                        {numberType === "constant" && countryLoading
                          ? (
                            <div className="flex items-center gap-2 text-xs text-text-tertiary-light dark:text-text-tertiary py-2">
                              <Spinner className="w-4 h-4 text-accent" />
                              <span>Checking number availability...</span>
                            </div>
                          )
                          : numberType === "constant" && availability
                          ? (
                            <div className="p-4 bg-accent/5 border border-accent/20 rounded-xl space-y-1.5">
                              <div className="flex items-center justify-between text-xs font-medium">
                                <span className="text-text-secondary-light dark:text-text-secondary">
                                  Availability:
                                </span>
                                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                  {availability.available > 0
                                    ? `${availability.available} numbers available`
                                    : "Out of stock"}
                                </span>
                              </div>
                              {availability.sampleNumber && (
                                <div className="flex items-center justify-between text-xs font-medium">
                                  <span className="text-text-secondary-light dark:text-text-secondary">
                                    Sample format:
                                  </span>
                                  <span className="font-mono text-text-primary-light dark:text-text-primary">
                                    +{availability.sampleNumber}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center justify-between text-xs font-medium">
                                <span className="text-text-secondary-light dark:text-text-secondary">
                                  Subscription:
                                </span>
                                {appliedPromo
                                  ? (
                                    <div className="text-right">
                                      <span className="line-through text-text-tertiary mr-1.5">
                                        ${availability.priceUsd}/mo
                                      </span>
                                      <span className="font-bold text-emerald-600 dark:text-emerald-400">
                                        $0 for 1st month
                                      </span>
                                      <span className="text-[10px] text-text-tertiary block">
                                        then ${availability.priceUsd}/month
                                      </span>
                                    </div>
                                  )
                                  : (
                                    <span className="font-bold text-accent">
                                      ${availability.priceUsd}/month
                                    </span>
                                  )}
                              </div>
                            </div>
                          )
                          : null}

                        <div className="p-3 bg-surface-overlay-light dark:bg-surface-overlay border border-border-subtle-light dark:border-border-subtle rounded-xl text-left space-y-1 text-xs">
                          <div className="font-semibold text-text-primary-light dark:text-text-primary flex items-center gap-1.5">
                            <span>🛡️</span>
                            <span>
                              Keep your WhatsApp number safe from bans
                            </span>
                          </div>
                          <p className="text-[11px] text-text-secondary-light dark:text-text-secondary leading-relaxed">
                            WhatsApp is very strict with automated lines. Never
                            use this number for spam or cold outreach. Sending
                            unsolicited messages to even 5 strangers who report
                            the chat can easily get your number permanently
                            banned or disconnected. Have contacts message the
                            bot first or save your bot's number.
                          </p>
                        </div>

                        <Button
                          onClick={handleBuyNumber}
                          disabled={isPurchasing ||
                            (numberType === "constant" &&
                              (countryLoading ||
                                availability?.available === 0)) ||
                            needsPhoneVerification ||
                            (numberType === "temporary" &&
                              !selectedTemporaryCountryId)}
                          className="w-full justify-center py-3 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md cursor-pointer"
                        >
                          {isPurchasing
                            ? (
                              <span className="flex items-center gap-2">
                                <Spinner className="w-4 h-4 text-white" />{" "}
                                Allocating Number...
                              </span>
                            )
                            : needsPhoneVerification
                            ? "Verify Mobile Phone Above to Continue"
                            : numberType === "temporary"
                            ? selectedTemporaryCountryId
                              ? "Get Free Temporary Number (SMS) →"
                              : "Select a Country Above to Continue"
                            : appliedPromo
                            ? "Get Permanent Dedicated Number (1st Month Free) →"
                            : (
                              `Get Permanent Number${
                                availability?.priceUsd
                                  ? ` ($${availability.priceUsd}/mo)`
                                  : ""
                              } →`
                            )}
                        </Button>
                      </div>
                    )
                    : (
                      <div className="space-y-4">
                        <p className="text-xs text-text-secondary-light dark:text-text-secondary leading-relaxed">
                          Have a spare SIM card or virtual number? Enter it in
                          full international format (e.g.{" "}
                          <code>+972501234567</code>).
                        </p>

                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-text-primary-light dark:text-text-primary">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            value={ownNumber}
                            onInput={(e) => setOwnNumber(e.currentTarget.value)}
                            placeholder="+972 50 123 4567"
                            className="w-full px-3 py-2 text-sm bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-xl text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent font-mono"
                          />
                        </div>

                        <Button
                          onClick={handleUseOwnNumber}
                          disabled={!ownNumber.trim()}
                          className="w-full justify-center py-3 text-sm font-semibold bg-accent hover:bg-accent-hover text-white shadow-md cursor-pointer"
                        >
                          Continue with this number →
                        </Button>
                      </div>
                    )}
                </div>
              )}

              {/* STEP 2: ACTIVATE ON PHONE */}
              {step === "activate_phone" && (
                <div className="space-y-5">
                  <div className="flex items-center justify-between p-3.5 bg-surface-overlay-light dark:bg-surface-overlay rounded-xl border border-border-subtle-light dark:border-border-subtle">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary block">
                        Your Dedicated Phone Number
                      </span>
                      <span className="text-base font-bold font-mono text-text-primary-light dark:text-text-primary">
                        +{activeNumber}
                      </span>
                    </div>
                    <CopyButton text={`+${activeNumber}`} />
                  </div>

                  {numberType === "temporary" && (
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-[11px] text-amber-900 dark:text-amber-200 space-y-1 leading-relaxed">
                      <p className="font-semibold">
                        This temporary number is not yours
                      </p>
                      <p>{temporaryNumberDisclaimer}</p>
                      <p>
                        Hit a cooldown timer or a block on WhatsApp?{" "}
                        {temporaryNumberAlreadyAllocated}
                      </p>
                    </div>
                  )}

                  {/* Step by step guide */}
                  <div className="space-y-3 p-4 bg-surface-overlay-light dark:bg-surface-overlay rounded-xl border border-border-subtle-light dark:border-border-subtle text-xs">
                    <div className="font-bold text-sm text-text-primary-light dark:text-text-primary flex items-center gap-2">
                      <span>📲</span> How to add this bot number in WhatsApp:
                    </div>

                    <div className="space-y-3 text-text-secondary-light dark:text-text-secondary leading-relaxed">
                      <div className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-accent text-white flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5">
                          1
                        </span>
                        <div>
                          <strong>Add account in WhatsApp:</strong>{" "}
                          Open WhatsApp on your phone → go to{" "}
                          <strong>Settings</strong> → tap the{" "}
                          <strong>arrow (⌄) next to your profile name</strong>
                          {" "}
                          → tap <strong>Add account</strong>{" "}
                          (or open WhatsApp Business).
                        </div>
                      </div>

                      <div className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-accent text-white flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5">
                          2
                        </span>
                        <div>
                          <strong>Enter the bot's number:</strong> Tap{" "}
                          <em>Agree and Continue</em>, select the country, and
                          paste{" "}
                          <code className="bg-surface px-1.5 py-0.5 rounded font-mono font-bold text-text-primary">
                            +{activeNumber}
                          </code>.
                          <div className="text-[11px] text-amber-600 dark:text-amber-400 font-semibold mt-0.5">
                            ⚠️ Enter the bot number above — do NOT enter your
                            personal number!
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-accent text-white flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5">
                          3
                        </span>
                        <div>
                          <strong>Enter verification code:</strong>{" "}
                          {numberType === "constant"
                            ? "Tap 'Call me' or 'Voice call'. We'll transcribe the code and display it below automatically!"
                            : "WhatsApp will send a 6-digit verification code. It will appear in the green listener below automatically! Enter it into WhatsApp to finish."}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* REAL-TIME SMS / VOICE CODE LISTENER */}
                  <div className="p-4 rounded-xl border border-emerald-300/60 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs font-semibold text-emerald-900 dark:text-emerald-200">
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75">
                          </span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500">
                          </span>
                        </span>
                        <span>
                          {numberType === "constant"
                            ? "Incoming Voice Call & Code Listener"
                            : "Incoming SMS Listener"}
                        </span>
                      </div>
                      <span className="text-[10px] text-emerald-800/70 dark:text-emerald-300/70">
                        Auto-updating
                      </span>
                    </div>

                    {latestCode
                      ? (
                        <div className="flex flex-col items-center gap-2 p-3 bg-white dark:bg-surface rounded-xl border border-emerald-300 dark:border-emerald-700 shadow-xs">
                          <span className="text-xs font-medium text-text-secondary-light dark:text-text-secondary">
                            Your WhatsApp Code:
                          </span>
                          <div className="flex items-center gap-3">
                            <span className="text-2xl font-bold font-mono tracking-widest text-emerald-700 dark:text-emerald-300">
                              {latestCode}
                            </span>
                            <CopyButton text={latestCode.replace(/\D/g, "")} />
                          </div>
                        </div>
                      )
                      : (
                        <div className="text-center py-3 text-xs text-emerald-800/80 dark:text-emerald-200/80 italic">
                          {numberType === "constant"
                            ? "Waiting for WhatsApp voice call... Tap 'Call me' or 'Voice call' in WhatsApp, and we'll automatically transcribe the code here!"
                            : "Waiting for WhatsApp to send SMS... Enter the number on your phone to trigger it."}
                        </div>
                      )}

                    {smsMessages.length > 0 && (
                      <div className="space-y-1.5 pt-2 border-t border-emerald-200/60 dark:border-emerald-800/60">
                        {smsMessages.map((m) => (
                          <div
                            key={m.id}
                            className="text-[11px] text-emerald-900/90 dark:text-emerald-100/90 bg-white/60 dark:bg-surface/60 p-2 rounded-lg font-mono"
                          >
                            {m.body}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => setStep("scan_qr")}
                      className="w-full justify-center py-3 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md cursor-pointer"
                    >
                      I activated WhatsApp on my phone → Next: Link Bot
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveNumber("");
                        setStep("choose_number");
                      }}
                      className="text-xs text-text-tertiary-light dark:text-text-tertiary hover:text-text-primary-light dark:hover:text-text-primary underline cursor-pointer text-center py-1"
                    >
                      ← Choose a different number or country
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3: SCAN QR / LINK BOT */}
              {step === "scan_qr" && (
                <div className="space-y-5 text-center">
                  {latestCode && (
                    <div className="flex items-center justify-between p-3.5 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-300 dark:border-emerald-800 rounded-xl text-xs text-left shadow-xs">
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-300 block">
                          Incoming WhatsApp SMS Code
                        </span>
                        <span className="text-xl font-bold font-mono tracking-wider text-emerald-700 dark:text-emerald-300">
                          {latestCode}
                        </span>
                      </div>
                      <CopyButton text={latestCode.replace(/\D/g, "")} />
                    </div>
                  )}

                  <div className="text-left space-y-2 p-3.5 bg-surface-overlay-light dark:bg-surface-overlay rounded-xl border border-border-subtle-light dark:border-border-subtle text-xs text-text-secondary-light dark:text-text-secondary leading-relaxed">
                    <div className="font-bold text-xs text-text-primary-light dark:text-text-primary flex items-center gap-2 mb-1">
                      <span>🔗</span> Link your new bot account:
                    </div>
                    <div>
                      <strong>1.</strong>{" "}
                      In WhatsApp on your device, make sure you are switched to
                      the <strong>new bot account</strong>.
                    </div>
                    <div>
                      <strong>2.</strong> Open <strong>Settings</strong>{" "}
                      (iOS) or <strong>⋮ Menu</strong> (Android) → tap{" "}
                      <strong>Linked Devices</strong> →{" "}
                      <strong>Link a Device</strong>.
                    </div>
                    <div>
                      <strong>3.</strong> {preferCode
                        ? "Enter this pairing code on your phone:"
                        : "Scan this QR code with your phone camera:"}
                    </div>
                  </div>

                  <div className="flex flex-col items-center justify-center min-h-[240px] p-6 bg-white dark:bg-surface rounded-2xl border border-border-subtle-light dark:border-border-subtle shadow-xs">
                    {isProvisioning
                      ? (
                        <div className="flex flex-col items-center gap-3 py-8">
                          <Spinner className="w-8 h-8 text-accent" />
                          <span className="text-xs text-text-secondary-light dark:text-text-secondary font-medium">
                            Initializing cloud session for +{activeNumber}...
                          </span>
                        </div>
                      )
                      : preferCode
                      ? (
                        pairingCode
                          ? (
                            <div className="flex flex-col items-center gap-3">
                              <span className="text-xs font-semibold uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary">
                                Enter Pairing Code:
                              </span>
                              <div className="text-3xl font-bold font-mono tracking-wider bg-surface-overlay-light dark:bg-surface-overlay px-6 py-3 rounded-xl border border-border-default-light dark:border-border-default text-text-primary-light dark:text-text-primary">
                                {pairingCode}
                              </div>
                              <CopyButton text={pairingCode} />
                            </div>
                          )
                          : (
                            <div className="text-xs text-text-secondary">
                              Requesting pairing code...
                            </div>
                          )
                      )
                      : qrSvg
                      ? (
                        <div className="flex flex-col items-center gap-2">
                          <div
                            className="w-[220px] h-[220px] flex items-center justify-center"
                            dangerouslySetInnerHTML={{ __html: qrSvg }}
                          />
                          <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                            Scan with WhatsApp camera
                          </span>
                        </div>
                      )
                      : (
                        <div className="flex flex-col items-center gap-2 py-8">
                          <Spinner className="w-6 h-6 text-accent" />
                          <span className="text-xs text-text-secondary">
                            Generating QR code...
                          </span>
                        </div>
                      )}
                  </div>

                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => setPreferCode(!preferCode)}
                      className="text-xs text-accent underline font-medium cursor-pointer hover:opacity-80"
                    >
                      {preferCode
                        ? "Switch to QR code scan"
                        : "On the same phone? Get an 8-character Pairing Code instead"}
                    </button>
                  </div>

                  <div className="flex items-center justify-center gap-2 text-xs text-text-tertiary-light dark:text-text-tertiary pt-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75">
                      </span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500">
                      </span>
                    </span>
                    <span>
                      Waiting for connection... Will automatically advance when
                      linked.
                    </span>
                  </div>

                  <div className="flex items-center justify-center pt-2 border-t border-border-subtle-light dark:border-border-subtle text-xs">
                    <button
                      type="button"
                      onClick={() => setStep("activate_phone")}
                      className="text-accent underline font-medium cursor-pointer hover:opacity-80"
                    >
                      ← Back to SMS Verification Code / Number Details
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 4: CONNECTED / SUCCESS */}
              {step === "connected" && (
                <div className="space-y-6 text-center py-4">
                  <div className="w-16 h-16 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center text-3xl mx-auto border border-emerald-500/30">
                    ✓
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-text-primary-light dark:text-text-primary font-display">
                      Bot is live on WhatsApp!
                    </h3>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary max-w-md mx-auto leading-relaxed">
                      {numberType === "temporary"
                        ? "Your temporary number "
                        : "Your dedicated number "}
                      <strong>+{activeNumber}</strong>{" "}
                      is connected and ready to respond to messages.
                    </p>
                    {numberType === "temporary" && (
                      <p className="text-[11px] text-amber-700 dark:text-amber-300 max-w-md mx-auto leading-relaxed bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5 text-left">
                        {temporaryNumberDisclaimer}
                      </p>
                    )}
                    <div className="p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-xl text-left space-y-1.5 max-w-md mx-auto text-xs">
                      <div className="flex items-center gap-1.5 font-semibold text-amber-900 dark:text-amber-200">
                        <span>⚠️</span>
                        <span>Protect your WhatsApp line from bans</span>
                      </div>
                      <p className="text-[11px] text-text-secondary-light dark:text-text-secondary leading-relaxed">
                        WhatsApp actively blocks accounts for spam. Sending
                        unsolicited messages to even 5 strangers can easily
                        cause the number to be blocked or disconnected
                        immediately. Only message people who expect to hear from
                        your bot, or ask them to message the bot first.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                    <a
                      href={`https://wa.me/${activeNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto px-6 py-3 rounded-xl font-semibold text-sm bg-emerald-600 hover:bg-emerald-700 text-white transition-colors cursor-pointer shadow-md inline-flex items-center justify-center gap-2"
                    >
                      <span>Open WhatsApp Chat</span>
                      <span>↗</span>
                    </a>
                    <Button
                      type="button"
                      onClick={onClose}
                      className="w-full sm:w-auto px-6 py-3 rounded-xl text-sm"
                    >
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
      </div>
    </div>
  );
};
